<!--
App.vue - Root Component
Gleem Core
-->

<script setup lang="ts">
import { ref, computed, onMounted, onErrorCaptured, onUnmounted } from 'vue'
import { RouterView } from 'vue-router'
import ToastContainer from '@/core/components/ToastContainer.vue'
import ErrorModal from '@/core/components/ErrorModal.vue'
import SupportForm from '@/core/components/SupportForm.vue'
import ConfirmDialog from '@/core/components/ConfirmDialog.vue'
import OfflineBanner from '@/core/components/OfflineBanner.vue'
import SWUpdatePrompt from '@/core/components/SWUpdatePrompt.vue'
import { Download, X, AlertTriangle } from 'lucide-vue-next'

// PWA Install Prompt
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[]
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
  prompt(): Promise<void>
}

const deferredPrompt = ref<BeforeInstallPromptEvent | null>(null)
const showInstallBanner = ref(false)
const isStandalone = ref(false)

// Error Boundary
interface GleemErrorDetail {
  error_ref: string | null
  status: number
  message: string
}

const errorState = ref({
  visible: false,
  errorRef: null as string | null,
  message: '',
  stack: ''
})

const supportFormVisible = ref(false)
const supportFormErrorRef = ref<string | null>(null)
const supportFormErrorMessage = ref('')

function handleGlobalError(event: Event) {
  const detail = (event as CustomEvent<GleemErrorDetail>).detail
  errorState.value = {
    visible: true,
    errorRef: detail.error_ref,
    message: detail.message,
    stack: ''
  }
}

function closeErrorModal() {
  errorState.value.visible = false
}

function openSupportForm(errorRef: string | null) {
  supportFormErrorRef.value = errorRef
  // Include stack trace in the error message for the support form
  const fullError = errorState.value.stack
    ? `${errorState.value.message}\n\nStack:\n${errorState.value.stack}`
    : errorState.value.message
  supportFormErrorMessage.value = fullError
  supportFormVisible.value = true
}

function closeSupportForm() {
  supportFormVisible.value = false
  supportFormErrorRef.value = null
  supportFormErrorMessage.value = ''
}

onErrorCaptured((err) => {
  console.error('Vue error captured:', err)
  const errorMessage = err instanceof Error ? err.message : String(err)
  const errorStack = err instanceof Error && err.stack ? err.stack : ''
  errorState.value = {
    visible: true,
    errorRef: null,
    message: errorMessage || 'Si è verificato un errore nell\'applicazione.',
    stack: errorStack
  }
  return false // prevent propagation
})

// DEV MODE Detection
const isDev = computed(() => {
  return window.location.hostname.includes('dev.gleemerp.com') ||
         window.location.hostname === 'localhost' ||
         window.location.hostname === '127.0.0.1'
})

onMounted(() => {
  // DEV MODE console log
  if (isDev.value) {
    console.log('%c🔧 DEV MODE ACTIVE', 'background: #f59e0b; color: white; padding: 8px 16px; font-size: 16px; font-weight: bold; border-radius: 4px;')
    console.log('%cEnvironment: Development', 'color: #f59e0b; font-weight: bold;')
    console.log('%cBackend: http://127.0.0.1:8012', 'color: #10b981;')
    console.log('%cFrontend: Hot-reload enabled (Vite)', 'color: #10b981;')
  }
  // Check if already installed as PWA
  isStandalone.value = window.matchMedia('(display-mode: standalone)').matches ||
                       ('standalone' in window.navigator && (window.navigator as Navigator & { standalone?: boolean }).standalone === true)

  // Listen for install prompt
  window.addEventListener('beforeinstallprompt', (e: Event) => {
    e.preventDefault()
    deferredPrompt.value = e as BeforeInstallPromptEvent
    // Show banner if not already installed (mobile + tablet, including landscape tablets up to 1400px)
    const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0
    if (!isStandalone.value && (window.innerWidth < 1400 || hasTouch)) {
      // Delay showing banner for better UX
      setTimeout(() => {
        showInstallBanner.value = true
      }, 5000)
    }
  })

  // Global error event listener
  window.addEventListener('gleem:error', handleGlobalError)

  // SW update handling is now owned by <SWUpdatePrompt /> which listens for
  // the `gleem:sw-update-available` event dispatched from main.ts.

  // Listen for successful install
  window.addEventListener('appinstalled', () => {
    console.log('PWA installed successfully')
    showInstallBanner.value = false
    deferredPrompt.value = null
  })
})

async function installApp() {
  if (!deferredPrompt.value) return

  deferredPrompt.value.prompt()
  const { outcome } = await deferredPrompt.value.userChoice

  if (outcome === 'accepted') {
    console.log('User accepted PWA install')
  }

  deferredPrompt.value = null
  showInstallBanner.value = false
}

onUnmounted(() => {
  window.removeEventListener('gleem:error', handleGlobalError)
})

function dismissBanner() {
  showInstallBanner.value = false
  // Don't show again for this session
  sessionStorage.setItem('pwa-banner-dismissed', 'true')
}
</script>

<template>
  <div id="app" class="min-h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-white font-sans transition-colors duration-300">
    <!-- DEV MODE Banner -->
    <div
      v-if="isDev"
      class="fixed top-0 left-0 right-0 z-[9999] bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg"
    >
      <div class="container mx-auto px-4 py-2 flex items-center justify-center gap-2 text-sm font-bold">
        <AlertTriangle class="w-4 h-4" />
        <span>🔧 DEV MODE ACTIVE</span>
        <span class="hidden md:inline text-xs font-normal opacity-90">
          • Hot-reload enabled • Backend: 127.0.0.1:8012
        </span>
      </div>
    </div>

    <!-- Offline Banner -->
    <OfflineBanner />

    <!-- Main content with padding if dev banner visible -->
    <router-view v-slot="{ Component, route }" :class="{ 'pt-10': isDev }">
      <transition name="page" mode="out-in">
        <component :is="Component" :key="route.fullPath" />
      </transition>
    </router-view>
    <ToastContainer />

    <!-- Error Modal (Global Error Boundary) -->
    <ErrorModal
      :error="errorState"
      @close="closeErrorModal"
      @report-problem="openSupportForm"
    />

    <!-- Confirm Dialog (Global) -->
    <ConfirmDialog />

    <!-- Service Worker update prompt (only fires when VITE_ENABLE_SW=true) -->
    <SWUpdatePrompt />

    <!-- Support Form Modal -->
    <SupportForm
      :visible="supportFormVisible"
      :error-ref="supportFormErrorRef"
      :error-message="supportFormErrorMessage"
      @close="closeSupportForm"
    />

    <!-- PWA Install Banner (Mobile only) -->
    <Transition
      enter-active-class="transition-transform duration-300 ease-out"
      enter-from-class="translate-y-full"
      enter-to-class="translate-y-0"
      leave-active-class="transition-transform duration-200 ease-in"
      leave-from-class="translate-y-0"
      leave-to-class="translate-y-full"
    >
      <div
        v-if="showInstallBanner"
        class="fixed bottom-20 left-4 right-4 z-[100] bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-slate-700 p-4 lg:hidden"
      >
        <div class="flex items-start gap-3">
          <div class="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-xl flex items-center justify-center flex-shrink-0">
            <Download class="w-6 h-6 text-primary-600 dark:text-primary-400" />
          </div>
          <div class="flex-1 min-w-0">
            <h3 class="font-bold text-gray-900 dark:text-white">Installa GLEEM</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Aggiungi alla schermata home per un accesso rapido
            </p>
          </div>
          <button
            @click="dismissBanner"
            class="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <X class="w-5 h-5" />
          </button>
        </div>
        <div class="flex gap-2 mt-3">
          <button
            @click="dismissBanner"
            class="flex-1 py-2.5 px-4 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-slate-700 rounded-xl hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors"
          >
            Non ora
          </button>
          <button
            @click="installApp"
            class="flex-1 py-2.5 px-4 text-sm font-bold text-white bg-primary-600 rounded-xl hover:bg-primary-700 transition-colors flex items-center justify-center gap-2"
          >
            <Download class="w-4 h-4" />
            Installa
          </button>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style>
/* Global styles will be in main.css */
</style>




